import { Code, ConnectError } from "@connectrpc/connect";
import type { Query, QueryKey } from "@tanstack/react-query";

export const isUnauthorizedError = (error: unknown) => {
    return error instanceof ConnectError && error.code === Code.Unauthenticated;
};

export const isNotFoundError = (error: unknown) => {
    return error instanceof ConnectError && error.code === Code.NotFound;
};

export const isPermissionDeniedError = (error: unknown) => {
    return error instanceof ConnectError && error.code === Code.PermissionDenied;
};

export const isInvalidArgumentError = (error: unknown) => {
    return error instanceof ConnectError && error.code === Code.InvalidArgument;
};

export const defaultRetry = (failureCount: number, error: Error) => {
    if (error instanceof ConnectError) {
        switch (error.code) {
            case Code.DeadlineExceeded:
                return true;
            case Code.Unauthenticated:
            case Code.PermissionDenied:
            case Code.NotFound:
            case Code.InvalidArgument:
            case Code.FailedPrecondition:
            case Code.Internal:
            case Code.Unimplemented:
                return false;
        }
    }
    return failureCount < 3;
};

/**
 * Default refetch interval for lists.
 * Refetch every 5 minutes, but if the data is older than 5 minutes, refetch every 10 seconds.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const defaultRefetchLists = (query: Query<any, Error, any, QueryKey>) => {
    const now = Date.now();
    const deltaTime = now - (query.state.dataUpdatedAt || now);
    if (deltaTime > 1_000 * 60 * 5) {
        return 1_000 * 10; // 10 seconds
    }
    return 1_000 * 60 * 5; // 5 minutes
};

/**
 From docs:

 > If set to `true` [...], all errors will be thrown to the error boundary.


 So, we carefully select exceptional errors to throw by default,
 and let the global error boundary handle them.

 In any other case, it should be up to the subscribing component
 to handle error states locally.
 */
export const defaultThrowOnError = (error: Error) => {
    if (error instanceof ConnectError) {
        switch (error.code) {
            case Code.DeadlineExceeded:
            case Code.Unauthenticated:
            case Code.PermissionDenied:
            case Code.NotFound:
            case Code.InvalidArgument:
            case Code.FailedPrecondition:
                return false;
            case Code.Internal:
                return true;
        }
    }
    return false;
};
